import React from "react";

import moment from "moment";
import { Link } from "react-router-dom";

import { OrganizationProjectStatusesTypes } from "Api/organizations/projects/OrganizationProjects.types";

import * as S from "./ProjectCard.styles";
import ProjectDetailsCard from "./ProjectDetailsCard";
import ProjectListItem from "./ProjectListItem";
import { useRegionLabel } from "./useRegionLabel";

import type { OrganizationProject } from "Api/organizations/projects/OrganizationProjects.types";

type Props = {
  gridLayout: boolean;
  hasProjectAccess: boolean;
  organizationLabel: string;
  organizationName: string;
  projectId: string;
  projectPlan: string;
  projectRegion: string;
  projectStatus: string;
  projectSubscriptionId: string;
  projectTitle: string;
  projectType: string;
  shouldShowProjectOwner: boolean;
  projectCreatedAt: string;
  refetchOrganizationProjectList: () => void;
  project: OrganizationProject;
};

const ProjectCard = ({
  gridLayout,
  hasProjectAccess,
  organizationLabel,
  organizationName,
  projectId,
  projectPlan,
  projectRegion,
  projectStatus,
  projectSubscriptionId,
  projectTitle,
  projectType,
  shouldShowProjectOwner,
  projectCreatedAt,
  refetchOrganizationProjectList,
  project
}: Props) => {
  const regionLabel = useRegionLabel(projectRegion);
  const projectIsProvisioning =
    projectStatus === OrganizationProjectStatusesTypes.requested;
  const projectIsInProvisioningFailure =
    projectStatus === OrganizationProjectStatusesTypes.failed;
  const Container =
    hasProjectAccess && !projectIsProvisioning ? Link : S.NoAccessWrapper;
  const isProjecCreatedRecently =
    moment(projectCreatedAt).diff(moment(), "minutes") >= -15;

  if (
    (projectIsProvisioning || projectIsInProvisioningFailure) &&
    !isProjecCreatedRecently
  )
    return <></>;

  return (
    <S.ProjectCardLayout
      hasGitProjectAccess={hasProjectAccess}
      className={gridLayout ? "project-card" : "project-list-item"}
    >
      {gridLayout ? (
        <Container
          to={`/${organizationName}/${projectId}`}
          id={`project-card-${projectId}`}
        >
          <ProjectDetailsCard
            hasProjectAccess={hasProjectAccess}
            organizationLabel={organizationLabel}
            projectId={projectId}
            projectRegion={projectRegion}
            projectStatus={projectStatus}
            projectSubscriptionId={projectSubscriptionId}
            projectTitle={projectTitle}
            projectType={projectType}
            organizationName={organizationName}
            refetchOrganizationProjectList={refetchOrganizationProjectList}
            projectCreatedAt={projectCreatedAt}
            project={project}
          />
        </Container>
      ) : (
        <ProjectListItem
          shouldShowProjectOwner={shouldShowProjectOwner}
          hasProjectAccess={hasProjectAccess}
          organizationLabel={organizationLabel}
          organizationName={organizationName}
          projectId={projectId}
          projectPlan={projectPlan}
          projectSubscriptionId={projectSubscriptionId}
          projectRegion={projectRegion}
          projectTitle={projectTitle}
          projectStatus={projectStatus}
          projectType={projectType}
          regionSuffix={regionLabel.suffix}
          regionTitle={regionLabel.title}
          refetchOrganizationProjectList={refetchOrganizationProjectList}
          projectCreatedAt={projectCreatedAt}
          project={project}
        />
      )}
    </S.ProjectCardLayout>
  );
};

export default React.memo(ProjectCard);
